/* The clips a crew kept, and the one control that makes another one.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A PAGE
 *
 * The obvious build is a Klipp tab. It cannot be: the customer's mobile tab bar
 * already holds its five slots -- Farms, Listen, Log, Feeding, Lice -- and
 * Shell.tsx is explicit that those five are "the five things people came for".
 * A sixth would push the bar into a scroll or an overflow, which costs every
 * customer something on every visit to pay for a feature most of them use once
 * a week.
 *
 * So the clips live where the listening happens, which is where every product
 * that solves this puts them. Maps does not have a Saved tab; the pins are on
 * the map you are already on and the list pulls up from the bottom of it.
 * Photos does not have a Favourites tab; the heart is on the photo. YouTube
 * puts chapters on the scrubber under the video. In each case the saved thing
 * is a STATE OF THE THING YOU ARE LOOKING AT rather than a destination you
 * navigate to, and the whole cost of the feature is one row of chrome.
 *
 * ---------------------------------------------------------------------------
 * AND WHY THE SHELF IS NOT JUST THE DAY STRIP
 *
 * The strip already draws a mark per clip, and that is genuinely the album on
 * the day you are standing in. But it is one day wide, and the clip somebody
 * most wants is the one from three weeks ago that they half remember. A list
 * that shared the strip's window would hide exactly the clips worth having
 * kept, so this one is fetched by SITE and ignores the day entirely -- and
 * pressing one moves the page to its day rather than the other way round.
 *
 * ---------------------------------------------------------------------------
 * SHARING WITH THE CREW IS NOT A BUTTON HERE
 *
 * A clip is addressed by farm, and every colleague scoped to that farm reads
 * this same list -- so it is shared with them the instant it is saved. What
 * Copy link copies is therefore an ordinary deep link into this page, not a
 * token: whoever opens it is authorised the way every other page authorises
 * them.
 *
 * Sharing OUTSIDE the company is the one thing that is a button: "Share with
 * anyone" opens DelSheet, which says exactly what the recipient will and will
 * not see before it mints a token link to /delt/<token> -- this clip, and only
 * this clip, without a sign-in.
 */
import { useEffect, useRef, useState } from 'react'
import { klipp, type Me, type SiteClip } from '../api'
import { useT } from '../lang'
import { DelSheet } from './DelSheet'
import { Menu } from './Menu'
import { clock, dayKey, dayLabel, today } from '../time'

/** `14:32 · 45 s`, and the day when it is not the day on screen.
 *
 *  The day is omitted for today and named for everything else, because the
 *  shelf is mostly read while standing in the day the clip came from -- and
 *  "i dag" printed on every chip is a word that never varies, which is a word
 *  that carries nothing. */
export function clipWhen(c: SiteClip): string {
  const at = new Date(c.started_utc).getTime() / 1000
  const day = dayKey(at)
  const secs = Math.round(c.duration_s)
  const span = secs >= 90 ? `${Math.round(secs / 60)} min` : `${secs} s`
  return day === today()
    ? `${clock(at)} · ${span}`
    : `${dayLabel(at)} ${clock(at)} · ${span}`
}

/** Their own name if they set one, otherwise the part of the address before
 *  the @. Never the whole address: a list of clips is not a place to publish
 *  six colleagues' email addresses to each other. */
function who(c: SiteClip): string {
  return c.created_by_name || c.created_by.split('@')[0] || c.created_by
}

/* ===================================================================== shelf
 */

export function ClipShelf({
  clips, me, playing, onOpen, onChanged, onError,
}: {
  clips: SiteClip[]
  me: Me
  /** The clip currently loaded in the player, so the shelf can say which one
   *  you are hearing. Without it, pressing a chip produces sound with nothing
   *  on screen connecting the two. */
  playing: string | null
  onOpen: (c: SiteClip) => void
  onChanged: () => void
  onError: (m: string) => void
}) {
  const t = useT()

  if (!clips.length) return null

  return (
    <section className="klipp">
      <h2 className="label">
        {t('Clips')} <span className="klipp-count num">{clips.length}</span>
      </h2>
      {/* A row that scrolls sideways rather than a grid that grows downwards.
          The shelf sits between the strip and the rest of the page, and a
          block that changes height as clips accumulate would push the page
          around under somebody's thumb every time a colleague saved one. */}
      <ul className="klipp-shelf">
        {clips.map((c) => (
          <li key={c.clip_id}>
            <ClipCard
              clip={c}
              me={me}
              on={c.clip_id === playing}
              onOpen={onOpen}
              onChanged={onChanged}
              onError={onError}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

function ClipCard({
  clip: c, me, on, onOpen, onChanged, onError,
}: {
  clip: SiteClip
  me: Me
  on: boolean
  onOpen: (c: SiteClip) => void
  onChanged: () => void
  onError: (m: string) => void
}) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(c.note ?? '')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const mayShare = me.clip_share_links === true && me.can?.share === true
  const [sharing, setSharing] = useState(false)

  /* Whose clip this is, and therefore whether the pen is offered.
   *
   * The server decides this too and will refuse either way; this only decides
   * whether to draw a control that would be refused. A company manager gets it
   * on everybody's, because somebody has to be able to tidy up after a person
   * who has left. */
  const mine = c.created_by_id === me.user_id
  const may = mine || me.can?.manage_people === true

  const save = async () => {
    setBusy(true)
    try {
      await klipp.note(c.clip_id, note.trim() || null)
      setEditing(false)
      onChanged()
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await klipp.remove(c.clip_id)
      onChanged()
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /* An ordinary deep link into this page, and deliberately not a share token.
   * Whoever opens it signs in and is scoped exactly as they always were, so a
   * link forwarded outside the company grants nothing. */
  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}`
      + `?klipp=${encodeURIComponent(c.clip_id)}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // in-app browsers. Saying so beats a button that silently does nothing.
      onError(t('Could not copy. Copy the address bar instead.'))
    }
  }

  if (editing) {
    return (
      <div className="klipp-card editing">
        <NoteField value={note} onChange={setNote} onSubmit={save} />
        <div className="klipp-actions">
          <button type="button" className="btn sm" disabled={busy} onClick={save}>
            {t('Save')}
          </button>
          <button type="button" className="btn ghost sm"
            onClick={() => { setNote(c.note ?? ''); setEditing(false) }}>
            {t('Cancel')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`klipp-card${on ? ' on' : ''}`}>
      <button type="button" className="klipp-play" onClick={() => onOpen(c)}
        aria-label={`${t('Play')} ${clipWhen(c)}`}>
        <span className="klipp-when num">{clipWhen(c)}</span>
        {/* The note is the whole point of the card, so it is the biggest thing
            on it. A clip with none says so rather than leaving a hole -- an
            unlabelled clip is still findable by its time, and pretending
            otherwise would push people to write "test" in the box. */}
        <span className={`klipp-note${c.note ? '' : ' none'}`}>
          {c.note || t('No note')}
        </span>
        <span className="klipp-who muted">{who(c)}</span>
      </button>
      {sharing && <DelSheet clip={c} onClose={() => setSharing(false)} />}
      <div className="klipp-tools">
        <Menu label={t('Clip')} busy={busy} actions={[
          { label: copied ? t('Link copied') : t('Copy link'),
            onSelect: () => void copyLink() },
          ...(mayShare ? [
            { label: t('Share with anyone…'), onSelect: () => setSharing(true) },
          ] : []),
          ...(may ? [
            { label: c.note ? t('Edit note') : t('Add a note'),
              onSelect: () => setEditing(true) },
            { label: t('Remove'),
              confirm: t('Remove this clip?'),
              detail: t('It disappears from the farm’s list. The recording '
                        + 'itself is not touched.'),
              danger: true,
              onSelect: () => void remove() },
          ] : []),
        ]} />
      </div>
    </div>
  )
}

/* ================================================================ the saver
 *
 * What appears the moment somebody stops marking. One field and two buttons,
 * and the field is focused: the note is the entire difference between a clip a
 * colleague can act on and a timestamp they have to go and listen to, and a
 * box somebody has to find and click into is a box that stays empty.
 */

export function SaveMark({
  t0, t1, saving, error, onSave, onCancel,
}: {
  t0: number
  t1: number
  saving: boolean
  error: string | null
  onSave: (note: string) => void
  onCancel: () => void
}) {
  const t = useT()
  const [note, setNote] = useState('')
  const secs = Math.max(1, Math.round(t1 - t0))

  return (
    <div className="klipp-save" role="dialog" aria-label={t('Save clip')}>
      <p className="klipp-save-span">
        <strong className="num">{clock(t0)}–{clock(t1)}</strong>
        <span className="muted num">
          {' '}{secs >= 90 ? `${Math.round(secs / 60)} min` : `${secs} s`}
        </span>
      </p>
      {/* Said once, here, and nowhere else. Everyone at the farm reading the
          same list is the feature, but somebody typing a note needs to know it
          before they type rather than after. */}
      <p className="klipp-save-say muted">{t('Everyone at this farm will see it.')}</p>
      <NoteField value={note} onChange={setNote} onSubmit={() => onSave(note)} autoFocus />
      {error && <p className="klipp-save-err">{error}</p>}
      <div className="klipp-actions">
        <button type="button" className="btn primary sm" disabled={saving}
          onClick={() => onSave(note)}>
          {saving ? t('Saving') : t('Save clip')}
        </button>
        <button type="button" className="btn ghost sm" disabled={saving}
          onClick={onCancel}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  )
}

/** The note box, in both places it appears.
 *
 *  Enter saves and Shift+Enter breaks the line, which is the way round every
 *  message box works -- these are one sentence about what was heard, not
 *  paragraphs, and a note that needs a mouse to submit is a note that gets
 *  abandoned on a boat. */
function NoteField({
  value, onChange, onSubmit, autoFocus,
}: {
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
  autoFocus?: boolean
}) {
  const t = useT()
  const ref = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => { if (autoFocus) ref.current?.focus() }, [autoFocus])
  return (
    <textarea
      ref={ref}
      className="klipp-field"
      rows={2}
      maxLength={500}
      value={value}
      placeholder={t('What did you hear?')}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit() }
      }}
    />
  )
}
